import { Box, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import * as lodash from 'lodash';
import React, { useMemo, useState } from 'react';

import {
  consts as commonConsts,
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import {
  CreditAgreement,
  METADATA_DISPLAY_FIELDS,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { EditButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditButton';
import { EditMetadataModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditMetadataModal';

const { isNumber: _isNumber } = lodash;

const CreditAgreementMetadataHeader = () => (
  <Thead>
    <Tr>
      <Th width="30%">Property</Th>
      <Th width="70%">Value</Th>
    </Tr>
  </Thead>
);

const CreditAgreementMetadataBody = ({
  metadata,
  isWip,
}: {
  metadata: CreditAgreement.DomainEntity.CreditAgreementMetadata;
  isWip: boolean;
}) => {
  const metadataDisplayData = useMemo(() => {
    const adjustedMetadataFields = METADATA_DISPLAY_FIELDS.reduce<
      {
        key: string;
        value: string | number;
      }[]
    >((adjustedMetadataArray, metadataDisplayField) => {
      let value = metadata[metadataDisplayField];
      if (!value) {
        return adjustedMetadataArray;
      }

      const key =
        metadataDisplayField === 's3Path'
          ? 'S3 Path'
          : commonUtils.toTitleCase(metadataDisplayField);

      if (_isNumber(value) && metadataDisplayField === 'effectiveTo') {
        value =
          value === commonConsts.MAX_TIMESTAMP
            ? '-'
            : commonDate.epochToLocaleDateString(value);
      }

      if (_isNumber(value) && metadataDisplayField === 'effectiveFrom') {
        // only show value as NOW if the viewed credit agreement is the WIP
        value = isWip ? 'now' : commonDate.epochToLocaleDateString(value);
      }

      adjustedMetadataArray.push({ key, value });
      return adjustedMetadataArray;
    }, []);

    return adjustedMetadataFields;
  }, [isWip, metadata]);

  return (
    <Tbody>
      {metadataDisplayData.map((metadataProperty) => {
        return (
          <Tr key={metadataProperty.key}>
            <Td>{metadataProperty.key}</Td>
            <Td>{metadataProperty.value}</Td>
          </Tr>
        );
      })}
    </Tbody>
  );
};

type MetadataProps = {
  metadata: CreditAgreement.DomainEntity.CreditAgreementMetadata;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
};
export const Metadata = ({ metadata, wipConfig }: MetadataProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isWip = Boolean(wipConfig);

  return (
    <Box mb="8">
      <StickyHeaderSection
        labelText="Metadata"
        labelRightComponent={
          wipConfig && (
            <>
              <EditButton setIsOpen={setIsOpen} />
              <EditMetadataModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                wipConfig={wipConfig}
              />
            </>
          )
        }
      >
        <Box mt="10px">
          <Table width="50%" borderWidth="1px">
            <CreditAgreementMetadataHeader />
            <CreditAgreementMetadataBody metadata={metadata} isWip={isWip} />
          </Table>
        </Box>
      </StickyHeaderSection>
    </Box>
  );
};
